import { IBooking } from "../../Orchestrator/Types/Index";
import { AdmissionStatusEnum, AncillaryStatusEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * Active source-side ancillary statuses for carry-over detection.
 *
 * Matches `ACTIVE_ANCILLARY_STATUSES` in
 * `src/Actions/LogicActions/UpgradePremium/extractCarriedOverAddons.ts` — the
 * two consumers must agree on "what counts as a carried-over ancillary",
 * otherwise the modal locks a row that the merge / persist passes don't
 * recognise (or vice versa) and the user gets double-charged on the
 * mismatched corner.
 *
 * REFUNDED is intentionally absent: a refunded ancillary on the source
 * offer is gone and must not be treated as carried-over.
 */
const ACTIVE_CARRIED_OVER_STATUSES = new Set<string>([
  AncillaryStatusEnum.EXCHANGE_ONGOING,
  AncillaryStatusEnum.FULFILLED,
  AncillaryStatusEnum.CONFIRMED,
]);

/**
 * Backend behavior after `POST /bookings/{id}/exchange-operations` during
 * Upgrade-to-Premium:
 *
 *   1. Source offer's admissions + ancillaries flip to EXCHANGE_ONGOING.
 *   2. A new PREBOOKED upgrade offer is created.
 *   3. The original ancillaries are AUTO-LINKED (a.k.a. "transferred") onto
 *      the new PREBOOKED offer at full price — same status (PREBOOKED), same
 *      price, no discriminating field. Backend calls them "transferred".
 *
 * Consequence: any code path that scans PREBOOKED ancillaries without
 * filtering will treat the transferred twins as if they were user-added in
 * this session. That breaks in two ways:
 *
 *   - `mergeUpgradePaymentSummary` counts them into the addon total and
 *     double-charges the user (the source ancillaries were already paid for
 *     on the original booking).
 *   - `PersistAddonsOnConfirmAction` sweeps them into the delete-on-confirm
 *     pass and the backend rejects with `TRANSFERRED_ANCILLARY_CANNOT_BE_
 *     REMOVED` (HTTP 400).
 *
 * Discriminator: the auto-linked ancillary has a TWIN on the
 * EXCHANGE_ONGOING source offer with the same `(passengerId, type)`. The
 * modal enforces one of each `(type, passenger)` per direction, so the key
 * is unique and the join is safe.
 *
 * This helper returns the set of `"<passengerId>|<type>"` keys for fast
 * O(1) twin lookup during PREBOOKED-offer scans.
 */
export function buildCarriedOverAncillaryKeys(booking: IBooking | null | undefined): Set<string> {
  const keys = new Set<string>();
  if (!booking) return keys;
  for (const offer of booking.bookedOffers || []) {
    const isExchangeOngoing = offer.admissions?.some(
      (a) => a.status === AdmissionStatusEnum.EXCHANGE_ONGOING,
    );
    if (!isExchangeOngoing) continue;
    for (const anc of (offer.ancillaries || [])) {
      // Accept the same statuses extractCarriedOverAddons does (see
      // ACTIVE_CARRIED_OVER_STATUSES above). The offer's EXCHANGE_ONGOING
      // admission is the load-bearing signal; the ancillary status filter
      // exists only to drop REFUNDED items that no longer apply.
      if (!ACTIVE_CARRIED_OVER_STATUSES.has((anc as any)?.status)) continue;
      const pid = (anc as any)?.passengerIds?.[0];
      const type = (anc as any)?.type;
      if (!pid || !type) continue;
      keys.add(`${pid}|${type}`);
    }
  }
  return keys;
}

/**
 * Returns true if the given ancillary on a PREBOOKED upgrade offer is a
 * backend-auto-linked twin (i.e. has a matching `(passengerId, type)` in
 * the carried-over key set produced by {@link buildCarriedOverAncillaryKeys}).
 *
 * Use this to skip the ancillary anywhere it would otherwise be treated as
 * a user-added or user-removable PREBOOKED item.
 */
export function isCarriedOverAncillary(
  ancillary: { passengerIds?: string[]; type?: string } | null | undefined,
  carriedOverKeys: Set<string>,
): boolean {
  if (!ancillary) return false;
  const pid = ancillary.passengerIds?.[0];
  const type = ancillary.type;
  if (!pid || !type) return false;
  return carriedOverKeys.has(`${pid}|${type}`);
}
